import { cx } from '@emotion/css';
import { InfoOutlined } from '@mui/icons-material';
import { FormControl, Input, Radio, Tooltip } from '@sortlist-frontend/design-system/legacy';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { ChangeEvent, FC, MouseEventHandler, useState } from 'react';

import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';

const PRICES = [1000, 3000, 5000, 10000, 'custom'];

type PriceProps = {
  price: number | null;
  error?: string;
  className?: string;
  onChange: (price: number) => void;
};

const getSelectedStartingPrice = (price: number | null) => {
  if (!price) return;

  if (PRICES.includes(price)) return price;
  if (price > 0) return 'custom';
};

export const Price: FC<PriceProps> = (props: PriceProps) => {
  const { price, error, onChange, className } = props;
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  const [selectedStartingPrice, setSelectedStartingPrice] = useState<number | string | null | undefined>(
    getSelectedStartingPrice(price),
  );
  const [customPrice, setCustomPrice] = useState<number | null | undefined>(
    !PRICES.includes(price || 0) ? price : undefined,
  );

  const handleInputClick = () => {
    setSelectedStartingPrice('custom');
  };

  const handleStartingPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value === 'custom') {
      setSelectedStartingPrice('custom');
      onChange(undefined as unknown as number);
      return;
    }
    const price = parseInt(e.currentTarget.value, 10);
    setCustomPrice(null);
    setSelectedStartingPrice(price);
    onChange(price);
  };

  const handleCustomPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const price = parseInt(e.currentTarget.value);
    setCustomPrice(price);
    onChange(price);
  };

  return (
    <div className="layout-gt-sm-row layout-wrap layout-column mt-2" data-testid="price">
      {PRICES.map((price, index) => {
        return (
          <div
            key={index}
            className={cx(
              index + 1 !== PRICES.length && 'lg:mb-0 mb-4',
              index === 0 ? 'lg:pr-4 ' : 'lg:px-4',
              'px-O py-gt-xs-sm-0 layout-row layout-align-start-center',
            )}>
            <FormControl
              control={
                <Radio
                  id={`startingPrice-${index}`}
                  name={`startingPrice`}
                  value={price}
                  iconSize="xs"
                  checked={selectedStartingPrice === price}
                  onChange={handleStartingPriceChange}
                />
              }
              label={
                price === 'custom' ? (
                  <div className="layout-row layout-align-start-center">
                    €
                    <Input
                      type="number"
                      placeholder="20000"
                      value={customPrice ?? ''}
                      onChange={handleCustomPriceChange}
                      onClick={handleInputClick}
                      min="0"
                      className="ml-2"
                      error={(error && selectedStartingPrice === 'custom') as boolean}
                      data-testid="price-custom-input"
                    />
                  </div>
                ) : (
                  `€${price}`
                )
              }
            />
          </div>
        );
      })}
      {error && (
        <p className="small text-error mt-2 display-inline-block" data-testid="price-error-message">
          {error}
        </p>
      )}
    </div>
  );
};
